import clsx from "clsx";
import { useState } from "react";

import EmptyPlaceholder from "common/components/EmptyPlaceholder";
import SearchInput from "common/components/SearchInput";
import { Button } from "common/components/ui/Button";
import Skeleton from "common/components/ui/Skeleton";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";

import LeadListCreate from "./LeadListCreate";
import LeadListItem from "./LeadListItem";

import type { JSX } from "react";
import type { LeadList } from "../../types";

import NoResultsImage from "assets/images/empty-placeholders/lead-lists-not-found.png";
import PlaceholderImage from "assets/images/empty-placeholders/lists-grid.png";

interface LeadListsGridProps {
  isLoading: boolean;
  leadLists: LeadList[];
}

export default function LeadListsGrid({
  isLoading,
  leadLists,
}: LeadListsGridProps) {
  const isDesktop = useTwBreakpoint("lg");
  const [searchText, setSearchText] = useState("");

  const minListsForSearch = isDesktop ? 12 : 5;

  const filteredLeadLists = leadLists.filter((leadList) =>
    leadList.name.toLowerCase().includes(searchText.toLowerCase()),
  );

  if (!leadLists.length && !isLoading) {
    return (
      <EmptyPlaceholder
        title="No lists yet"
        subtitle="Create your first list to start collecting LinkedIn leads"
        imageSrc={PlaceholderImage}
        actionComponent={<LeadListCreate />}
      />
    );
  }

  let mainContent: JSX.Element | JSX.Element[];

  if (isLoading) {
    mainContent = new Array(8).fill({}).map((_, index) => (
      <div
        className="flex flex-col gap-y-4 rounded-5xl border border-black-200 p-4"
        // biome-ignore lint/suspicious/noArrayIndexKey: no key available
        key={index}
      >
        <Skeleton className="h-5 w-28" />

        <div className="flex flex-col gap-1">
          <Skeleton className="h-5 w-48" />
          <Skeleton className="h-4 w-32" />
        </div>

        <div className="flex flex-row justify-between items-center gap-4">
          <Skeleton className="h-5 w-28" />
          <Skeleton className="size-5" />
        </div>
      </div>
    ));
  } else if (!filteredLeadLists.length) {
    mainContent = (
      <EmptyPlaceholder
        title="No lists found"
        subtitle="There were no results that match your criteria"
        imageSrc={NoResultsImage}
        actionComponent={
          <Button onClick={() => setSearchText("")}>Clear</Button>
        }
      />
    );
  } else {
    mainContent = filteredLeadLists.map((leadList) => (
      <LeadListItem key={leadList.id} leadList={leadList} />
    ));
  }

  const hasNoResults = filteredLeadLists.length === 0 && searchText;

  return (
    <>
      {/* Keep visible while searchText is set, so an active filter always has a way to be cleared. */}
      {(leadLists.length >= minListsForSearch || searchText) && (
        <SearchInput
          className="lg:max-w-72"
          placeholder="Search lists"
          value={searchText}
          variant={isDesktop ? "sm" : "lg"}
          onChange={(event) => setSearchText(event.target.value)}
          onClear={() => setSearchText("")}
        />
      )}

      <div
        className={clsx([
          !hasNoResults &&
            "grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-4 gap-x-3 gap-y-3 lg:gap-y-6",
        ])}
      >
        {mainContent}
      </div>
    </>
  );
}
